import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { QuoteRepository, RoutesRepository, VoyagesRepository } from '@data-layer/repositories';
import {
  LclPortModel,
  PackageModel,
  PackageUpdateModel,
  QuoteManualModel,
  QuoteModel,
  QuoteUpdateModel,
  RouteModel,
  VoyageModel,
} from '@data-layer/models/lcl';
import { AbstractControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { FormHelper } from '@helpers/form.helper';
import { PaginatedResultModel } from '@data-layer/models/common';
import { debounceTime, filter, take, takeUntil } from 'rxjs/operators';
import { DateHelper } from '@helpers/date.helper';
import { CompanyModel } from '@data-layer/models/companies';
import { combineLatest, ReplaySubject } from 'rxjs';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { LclContainerService } from '@shared/lcl-mutual/services';
import { LclCreateQuotePackagesComponent } from './lcl-create-quote-packages/lcl-create-quote-packages.component';
import { enumShippingRole } from '@data-layer/enums/user';
import { enumVoyageStatus } from '@data-layer/enums/lcl';
import { UtilsHelper } from '@helpers/utils-helper';
import { GoogleAnalyticsService } from '@services/google-analytics.service';
import { enumGATagEventCategory } from '@enum/enumGATagEventCategory';
import { enumGATagEventAction } from '@enum/enumGATagEventAction';
import { UserModel } from '@data-layer/models/user';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { enumIncotermsType } from '@data-layer/enums/lcl/enumIncotermsType';

@Component({
  selector: 'app-lcl-create-quote',
  templateUrl: './lcl-create-quote.component.html',
  styleUrls: ['./lcl-create-quote.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LclCreateQuoteComponent implements OnInit, OnDestroy {
  @Input() public company: CompanyModel;
  @Input() public isHideLCLFooter: boolean;
  @Output() public createQuote: EventEmitter<QuoteUpdateModel> = new EventEmitter<QuoteUpdateModel>();
  @Output() public createManualQuote: EventEmitter<QuoteManualModel> = new EventEmitter<QuoteManualModel>();
  @Output() public getRoutes: EventEmitter<{ fromId: number; toId: number }> = new EventEmitter<{ fromId: number; toId: number }>();
  @Output() public updateQuoteDraft: EventEmitter<QuoteModel> = new EventEmitter<QuoteModel>();

  public formGroup: UntypedFormGroup;
  public packagesFormArray: UntypedFormArray;
  public isSubmitted: boolean;
  public isMobile: boolean;
  public isNotFirstResize: boolean;
  public possibleRoute: RouteModel;
  public isFormEdited: boolean;
  public totalValue: number = 0;
  public totalWeight: number = 0;
  public totalUnits: number = 0;

  public departurePortFc: UntypedFormControl;
  public arrivalPortFc: UntypedFormControl;

  public customerNotesFc: UntypedFormControl;

  public errors: IErrorForm = {
    required: 'LABELS.REQUIRED',
  };

  @ViewChild('packagesView', { static: false })
  public packagesView: LclCreateQuotePackagesComponent;

  public enumShippingRole: typeof enumShippingRole = enumShippingRole;

  private fromPort: LclPortModel;
  private toPort: LclPortModel;
  private isNotFoundRoutes: boolean;
  private isNotFoundRoutesWarningOpened: boolean;

  private destroy$: ReplaySubject<void> = new ReplaySubject<void>();

  constructor(
    private routesRepository: RoutesRepository,
    private voyagesRepository: VoyagesRepository,
    private quoteRepository: QuoteRepository,
    private fh: FormHelper,
    private lclContainerService: LclContainerService,
    private cdr: ChangeDetectorRef,
    private ga: GoogleAnalyticsService,
    private companiesService: CompaniesService,
    private genericService: GenericService,
  ) {}

  public ngOnInit(): void {
    this.formGroup = this.fh.createQuoteFormGroup(false);
    this.departurePortFc = this.formGroup.get(['shippingFrom']) as UntypedFormControl;
    this.departurePortFc.setValidators([Validators.required]);
    this.arrivalPortFc = this.formGroup.get(['shippingTo']) as UntypedFormControl;
    this.arrivalPortFc.setValidators([Validators.required]);
    this.customerNotesFc = new UntypedFormControl(null);

    this.packagesFormArray = this.formGroup.get('packages') as UntypedFormArray;

    this.formGroup.get('shippingRole').setValue(this.company?.shippingRole || enumShippingRole.IMPORTER);
    this.updateShippingRole();

    this.isFormEdited = this.checkIsFormEdited;

    combineLatest([this.departurePortFc.valueChanges, this.arrivalPortFc.valueChanges])
      .pipe(
        filter(([fromPortId, toPortId]: [number, number]) => !!fromPortId && !!toPortId),
        takeUntil(this.destroy$),
      )
      .subscribe(([fromId, toId]: [number, number]) => {
        this.getPossibleRoutes(fromId, toId);
      });

    this.genericService.isDesktop$.pipe(takeUntil(this.destroy$)).subscribe((isDesktop: boolean) => {
      this.isMobile = !isDesktop;

      if (this.isMobile && !this.isNotFirstResize) {
        this.removeQuote(0);
        this.packagesFormArray.updateValueAndValidity({ emitEvent: false });
      }

      this.isNotFirstResize = true;
    });

    this.calcTotals();

    this.packagesFormArray.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.calcTotals();
    });

    this.formGroup.valueChanges
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe((data: QuoteModel) => this.updateQuoteDraft.emit(data));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
  }

  public addPackage(): void {
    if (this.packagesFormArray.valid) {
      const firstPackage: PackageModel = this.packagesFormArray.getRawValue()[0];
      this.packagesFormArray.push(this.fh.createPackageFormGroup(firstPackage?.lengthUnit, firstPackage?.weightUnit));
    } else {
      this.markFormValidity();
    }
  }

  public confirmPackage(): void {
    if (this.packagesFormArray.valid) {
      this.packagesFormArray.controls.forEach((item: AbstractControl) => item.get('isView').setValue(true));
    } else {
      this.markFormValidity();
    }
  }

  public onSubmit(): void {
    this.isSubmitted = true;

    if (this.formGroup.invalid) {
      this.markFormValidity();
      return;
    }
    this.isNotFoundRoutesWarningOpened = true;
    this.saveQuote();
  }

  public removeQuote(index: number): void {
    this.packagesFormArray.removeAt(index);
  }

  public updateShippingRole(): void {
    switch (Number(this.formGroup.get('shippingRole').value)) {
      case enumShippingRole.IMPORTER:
        this.formGroup.get('incoTermsType').setValue(enumIncotermsType.FOB);
        break;
      case enumShippingRole.SHIPPER:
        this.formGroup.get('incoTermsType').setValue(enumIncotermsType.DAT);
        break;

      default:
        this.formGroup.get('incoTermsType').setValue(enumIncotermsType.FOB);
        break;
    }
  }

  public onSelectFromPort(port: LclPortModel): void {
    this.fromPort = port;
  }

  public onSelectToPort(port: LclPortModel): void {
    this.toPort = port;
  }

  public onCloseRouteWarning(): void {
    this.isNotFoundRoutesWarningOpened = false;
  }

  public onNotesSubmit(): void {
    if (this.formGroup.invalid || !this.packagesFormArray.controls.length) {
      if (!this.packagesFormArray.controls.length) {
        this.addPackage();
      }
      this.markFormValidity();
      return;
    }

    this.customerNotesFc.disable();

    const packages: PackageUpdateModel[] = this.lclContainerService.convertPackagesDataForCreateSending(
      this.packagesFormArray.value || [],
      true,
    );

    this.createManualQuote.emit({
      departurePortId: this.departurePortFc.value,
      arrivalPortId: this.arrivalPortFc.value,
      customerNotes: this.customerNotesFc.value,
      packages,
    });
  }

  public addLoadDetails(): void {
    this.addPackage();
    this.packagesView.forceUpdateForm();
  }

  public onStartSearch(): void {
    this.isNotFoundRoutes = false;
    this.isNotFoundRoutesWarningOpened = false;
  }

  public get isShowRouteWarning(): boolean {
    return this.isNotFoundRoutes && this.isNotFoundRoutesWarningOpened;
  }

  public get checkIsFormEdited(): boolean {
    return this.packagesFormArray.controls.some((item: AbstractControl) => !Boolean(item.get('isView').value));
  }

  private saveQuote(): void {
    const route: RouteModel = this.possibleRoute;

    if (!route || this.isNotFoundRoutes) {
      return;
    }

    this.voyagesRepository
      .searchByRouteId(route.id, {})
      .pipe(take(1))
      .subscribe((voyages: PaginatedResultModel<VoyageModel>) => {
        const packages: PackageUpdateModel[] = this.lclContainerService.convertPackagesDataForCreateSending(
          this.packagesFormArray.value || [],
          true,
        );

        const availableVoyages: VoyageModel[] = (voyages.items || []).filter(
          (item: VoyageModel) =>
            DateHelper.isSameOrAfter(item.validityDate, DateHelper.now) &&
            ![enumVoyageStatus.EXPIRED, enumVoyageStatus.OPTIMIZED].includes(item.status),
        );

        if (!availableVoyages?.length) {
          return;
        }

        this.createQuote.emit({
          incoTermsType: this.formGroup.get('incoTermsType').value,
          shippingRole: this.formGroup.get('shippingRole').value,
          voyageId: UtilsHelper.minElementBy<VoyageModel, string>(availableVoyages, 'estimatedDepartureTime', DateHelper.getTime)?.id,
          companyId: this.company?.id,
          packages,
        });
      });
  }

  private getPossibleRoutes(fromPortId: number, toPortId: number): void {
    this.routesRepository
      .searchV2({ fromPortId, toPortId })
      .pipe(takeUntil(this.destroy$))
      .subscribe((route: RouteModel) => {
        this.isNotFoundRoutes = false;
        this.customerNotesFc.enable();

        if (!!this.fromPort && !!this.fromPort) {
          this.sendGAEvent();
        }

        // find routes where voyage validityDate is sameOrAfter now
        this.possibleRoute = route;

        if (
          !this.possibleRoute ||
          !(this.possibleRoute?.voyages || []).some(
            (voyage: VoyageModel) =>
              DateHelper.isSameOrAfter(voyage.validityDate, DateHelper.now) &&
              ![enumVoyageStatus.EXPIRED, enumVoyageStatus.OPTIMIZED].includes(voyage.status),
          )
        ) {
          this.isNotFoundRoutes = true;
        }

        this.cdr.markForCheck();
      });
  }

  private sendGAEvent(): void {
    const user: UserModel = this.companiesService.getUser();

    this.ga.emitCustomEvent(
      enumGATagEventCategory.EezyFreight,
      enumGATagEventAction.EezyFreightSelectedRoute,
      `USER: ${user?.externalId}, FROM: ${this.fromPort.displayName} (${this.fromPort.unCode}), ${this.fromPort.country?.description}, TO: ${this.toPort.displayName} (${this.toPort.unCode}), ${this.toPort.country?.description}`,
    );
  }

  private markFormValidity(): void {
    this.fh.markAsTouched(this.formGroup);
    this.packagesView.forceUpdateForm();
    this.cdr.markForCheck();
  }

  private calcTotals(): void {
    const values: PackageUpdateModel[] = this.packagesFormArray.getRawValue();

    this.totalValue = this.lclContainerService.totalCBM(values);
    this.totalWeight = this.lclContainerService.totalWeight(values);
    this.totalUnits = this.lclContainerService.totalUnits(values);

    this.isFormEdited = this.checkIsFormEdited;
  }
}
